/**
 * DD-SUBSTRATE-RESOLVE-001 (F-057 / F-056) — substrate choices for the D&D Triage
 * picker, built from the SAME Sign Builder catalog the Sales form uses, grouped the
 * same way ("Recommended for this category" / "All other substrates").
 */
import {
  SUBSTRATES,
  formatSubstrateOptionLabel,
  getAvailableSubstrates,
  type MockSubstrate
} from '@/data/mockSignBuilderCatalog'

export interface CatalogPickOption {
  /** Catalog id (= PIQ code today) — stored as `<key>_id`. */
  id: string
  /** Display label in the list (Sales-form formatting: label + code + Local/SO suffixes). */
  label: string
  /** Plain catalog label — stored as the attribute value (matches intake's spec label). */
  storeLabel: string
}

export interface CatalogPickGroup {
  label: string
  options: CatalogPickOption[]
}

function toOption(sub: MockSubstrate): CatalogPickOption {
  return { id: sub.id, label: formatSubstrateOptionLabel(sub), storeLabel: sub.label }
}

/** Grouped substrate options for a category id ('' or 'other' → everything). */
export function substrateOptionGroups(categoryId: string | null | undefined): CatalogPickGroup[] {
  const cat = categoryId ?? ''
  const available = getAvailableSubstrates(cat)
  const recommended = cat ? available.filter((s) => s.cats?.includes(cat)) : []
  const others = cat ? available.filter((s) => !s.cats?.includes(cat)) : available
  const groups: CatalogPickGroup[] = []
  if (recommended.length) {
    groups.push({ label: 'Recommended for this category', options: recommended.map(toOption) })
  }
  if (others.length) {
    groups.push({
      label: recommended.length ? 'All other substrates' : 'All substrates',
      options: others.map(toOption)
    })
  }
  return groups
}

/** Catalog entry by id. */
export function findSubstrateById(id: string | null | undefined): MockSubstrate | undefined {
  if (!id) return undefined
  return SUBSTRATES.find((s) => s.id === id)
}

/**
 * Resolve a stored substrate to a catalog entry. The label is what humans see and
 * what the packet prints, so a label that matches the catalog wins over a stale
 * companion id (e.g. label edited elsewhere); otherwise the id resolves, and a
 * legacy free-text label (written before the picker existed) resolves by label.
 */
export function resolveStoredSubstrate(
  id: string | null | undefined,
  label: string | null | undefined
): MockSubstrate | undefined {
  const l = (label ?? '').trim().toLowerCase()
  const byLabel = l ? SUBSTRATES.find((s) => s.label.trim().toLowerCase() === l) : undefined
  const byId = findSubstrateById(id)
  if (byId && (!byLabel || byLabel.id === byId.id)) return byId
  return byLabel ?? byId
}

/**
 * F-038 — the exact PIQ code of a stored substrate (id + label resolved as above), or null
 * for a legacy free-text value that matches nothing. Display only: the attributes keep the
 * label and the catalog id.
 */
export function substrateCode(
  id: string | null | undefined,
  label: string | null | undefined
): string | null {
  const sub = resolveStoredSubstrate(id, label)
  return sub?.piq ? sub.piq : null
}
